import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshUser = useCallback(async () => {
        try {
            const res = await api.get("/auth/me");
            setUser(res.data);
            try { sessionStorage.setItem("tc_had_session", "1"); } catch { /* ignore */ }
            return res.data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // If returning from Google OAuth callback, skip the /auth/me check.
        // AuthCallback handles the session_id exchange first.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refreshUser().finally(() => setLoading(false));
    }, [refreshUser]);

    const loginWithEmail = async (email, password) => {
        const res = await api.post("/auth/login", { email, password });
        localStorage.setItem("tc_token", res.data.token);
        try { sessionStorage.setItem("tc_had_session", "1"); } catch { /* ignore */ }
        setUser(res.data.user);
        return res.data.user;
    };

    const registerWithEmail = async (email, password, name) => {
        const res = await api.post("/auth/register", { email, password, name });
        localStorage.setItem("tc_token", res.data.token);
        try { sessionStorage.setItem("tc_had_session", "1"); } catch { /* ignore */ }
        setUser(res.data.user);
        return res.data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem("tc_token");
        try { sessionStorage.removeItem("tc_had_session"); } catch { /* ignore */ }
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, loginWithEmail, registerWithEmail, logout, refreshUser, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
